'use client'

// React Imports
import { useState, useEffect, useMemo } from 'react'

// MUI Imports
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import TablePagination from '@mui/material/TablePagination'
import Box from '@mui/material/Box'

// Third-party Imports
import classnames from 'classnames'
import { Icon } from '@iconify/react'
import { rankItem } from '@tanstack/match-sorter-utils'
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    getPaginationRowModel,
    getSortedRowModel,
    getExpandedRowModel
} from '@tanstack/react-table'

import React from 'react'

// Style Imports
import tableStyles from '../../../../components/common/Template_Layouts/@core/styles/table.module.css'
import nodata from '../../../../assets/nodata.gif'
import Loader from '../../../../components/Loader/Loader'

const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
}

const PremiumModuleTable = (props) => {
    const {
        data: tableData,
        columns,
        totalRecords,
        currentPage,
        itemsPerPage,
        onPageChange,
        onRowSelectionChange,
        rowSelection: externalRowSelection,
        onManualRowSelectionChange,
        globalFilter: externalGlobalFilter,
        onGlobalFilterChange,
        loading = false,
        isLoading = false,
        hasFetched = true,
        isInitialLoad = false,
        onItemsPerPageChange,
        sorting: externalSorting,
        onSortingChange: externalOnSortingChange
    } = props;

    const isDataLoading = loading || isLoading;

    const onRowClick = props.onRowClick;

    const [internalRowSelection, setInternalRowSelection] = useState({})
    const rowSelection = externalRowSelection || internalRowSelection
    const setRowSelection = onManualRowSelectionChange || setInternalRowSelection

    const [internalSorting, setInternalSorting] = useState([])
    const sorting = externalSorting || internalSorting
    const setSorting = externalOnSortingChange || setInternalSorting

    const [globalFilter, setGlobalFilter] = useState(externalGlobalFilter || '')
    const [jumpPage, setJumpPage] = useState(currentPage || 1)

    useEffect(() => {
        setJumpPage(currentPage || 1)
    }, [currentPage])

    useEffect(() => {
        setGlobalFilter(externalGlobalFilter || '')
    }, [externalGlobalFilter])

    useEffect(() => {
        if (onRowSelectionChange) {
            onRowSelectionChange(rowSelection)
        }
    }, [rowSelection, onRowSelectionChange])

    const table = useReactTable({
        data: tableData || [],
        columns,
        filterFns: { fuzzy: fuzzyFilter },
        state: {
            rowSelection,
            globalFilter,
            sorting,
            pagination: {
                pageIndex: (currentPage || 1) - 1,
                pageSize: itemsPerPage || 10
            }
        },
        manualPagination: true,
        manualFiltering: true,
        getRowId: (row, relativeIndex) => row.id || row.idNum || row.TaskHistoryId || String(relativeIndex), // Support global selection via IDs
        pageCount: Math.ceil((totalRecords || 0) / (itemsPerPage || 10)),
        enableSorting: true,
        enableRowSelection: true,
        getRowCanExpand: props.getRowCanExpand,
        globalFilterFn: fuzzyFilter,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        onGlobalFilterChange: (val) => {
            setGlobalFilter(val)
            if (onGlobalFilterChange) onGlobalFilterChange(val)
        },
        onSortingChange: setSorting,
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getExpandedRowModel: getExpandedRowModel()
    })

    const startIndex = ((currentPage || 1) - 1) * (itemsPerPage || 10);
    // Use local slice if manual pagination is not handled by parent (though usually it is)
    const slicedRows = table.getRowModel().rows;

    return (
        <>
            <style>{`
            .premium-table-scroll-container::-webkit-scrollbar { width: 10px; height: 10px; }
            .premium-table-scroll-container::-webkit-scrollbar-track { background: #f8fafc; }
            .premium-table-scroll-container::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 5px; border: 2px solid #f8fafc; }
            .premium-table-scroll-container::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            .premium-table-row:hover { background-color: #f8fafc; }
        `}</style>
            <Card sx={{
                boxShadow: 'none', border: 'none', borderBottom: '1px solid #ebedf2',
                borderRadius: 0, width: '100%', margin: 0,
                display: 'flex', flexDirection: 'column',
                height: '100%',          // fill the parent flex cell
                flex: 1,                 // also fill via flex for nested flex contexts
                minHeight: 0,            // allow shrinking below content size
                overflow: 'hidden'       // clip only the table scroll area, not the footer
            }}>
                <div className="premium-table-scroll-container" style={{
                    flex: 1,
                    overflowY: 'scroll', /* Force scrollbar visibility */
                    overflowX: 'auto',
                    minHeight: 0,
                    backgroundColor: '#fff'
                }}>
                    <table className={tableStyles.table} style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#ffffff' }}>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <th key={header.id} style={{
                                            padding: '12px 16px',
                                            paddingLeft: header.id === 'select' ? '16px' : '16px',
                                            width: header.id === 'select' ? '52px' : 'auto',
                                            textAlign: 'left',
                                            backgroundColor: '#ffffff',
                                            borderBottom: '1px solid #e2e8f0'
                                        }}>
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    className={classnames('flex items-center select-none w-full gap-2', {
                                                        'cursor-pointer select-none': header.column.getCanSort(),
                                                        'justify-center': header.id === 'action',
                                                        'justify-between': header.id !== 'action'
                                                    })}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    <div className='text-[13px] font-bold uppercase tracking-wider text-[#666666]'>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </div>
                                                    {header.column.getCanSort() && (
                                                        <div
                                                            title={
                                                                header.column.getIsSorted() === 'asc'
                                                                    ? 'Sort descending'
                                                                    : header.column.getIsSorted() === 'desc'
                                                                        ? 'Sort ascending'
                                                                        : 'Sort by ascending/descending'
                                                            }
                                                            className={classnames('flex items-center transition-all duration-200', {
                                                                'text-[#3b82f6] opacity-100 scale-110 font-bold': header.column.getIsSorted(),
                                                                'text-[#64748b] opacity-100 hover:opacity-100 hover:text-[#3b82f6]': !header.column.getIsSorted()
                                                            })}
                                                        >
                                                            {{
                                                                asc: <Icon icon='ri:arrow-up-s-fill' fontSize={22} />,
                                                                desc: <Icon icon='ri:arrow-down-s-fill' fontSize={22} />
                                                            }[header.column.getIsSorted()] ?? <Icon icon='ri:arrow-up-down-fill' fontSize={22} />}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        {isDataLoading ? (
                            <tbody>
                                <tr>
                                    <td colSpan={columns.length + (props.rowSelection ? 1 : 0)} className='text-center py-10'>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                                            <Loader message="Loading data..." inline={true} />
                                        </Box>
                                    </td>
                                </tr>
                            </tbody>
                        ) : tableData.length === 0 && hasFetched ? (
                            <tbody>
                                <tr>
                                    <td colSpan={columns.length + (props.rowSelection ? 1 : 0)} className='text-center py-10'>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                                            <img
                                                src={nodata}
                                                alt="No Record Found"
                                                style={{ width: '350px', height: 'auto', marginBottom: '16px' }}
                                            />
                                            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#444' }}>
                                                No record Found.
                                            </Typography>
                                        </Box>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                {slicedRows.map(row => (
                                    <React.Fragment key={row.id}>
                                        <tr
                                            className={classnames('premium-table-row', { selected: row.getIsSelected() })}
                                            onClick={() => props.onRowClick && props.onRowClick(row)}
                                            style={{ cursor: props.onRowClick ? 'pointer' : 'default' }}
                                        >
                                            {row.getVisibleCells().map(cell => (
                                                <td
                                                    key={cell.id}
                                                    style={{
                                                        padding: '8px 16px',
                                                        paddingLeft: cell.column.id === 'select' ? '16px' : '16px',
                                                        width: cell.column.id === 'select' ? '52px' : 'auto',
                                                        borderBottom: '1px solid #ebedf2'
                                                    }}
                                                    className={classnames({ 'text-center': cell.column.id === 'action' || cell.column.id === 'expand' })}
                                                >
                                                    <Box
                                                        onClick={cell.column.id === 'select' || cell.column.id === 'action' || cell.column.id === 'expand' ? (e) => e.stopPropagation() : undefined}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </Box>
                                                </td>
                                            ))}
                                        </tr>
                                        {row.getIsExpanded() && props.renderSubComponent && (
                                            <tr>
                                                <td colSpan={row.getVisibleCells().length} style={{ padding: 0, borderBottom: '1px solid #ebedf2', backgroundColor: '#f8fafc' }}>
                                                    {props.renderSubComponent({ row })}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        )}
                    </table>
                </div>
                <div className='flex items-center justify-end px-4 gap-4 border-t py-2' style={{ backgroundColor: '#fcfcfc' }}>
                    <div className='flex items-center gap-2'>
                        <Typography variant='body2' className='text-xs opacity-70'>
                            Go to page
                        </Typography>
                        <input
                            type='number'
                            value={jumpPage}
                            min={1}
                            max={Math.ceil((totalRecords || 0) / (itemsPerPage || 10))}
                            onChange={e => setJumpPage(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const maxPage = Math.ceil((totalRecords || 0) / (itemsPerPage || 10));
                                    const page = Math.max(1, Math.min(Number(jumpPage), maxPage));
                                    onPageChange && onPageChange(page);
                                    setJumpPage(page);
                                    e.target.blur();
                                }
                            }}
                            onBlur={e => {
                                const page = Math.max(1, Math.min(Number(e.target.value), Math.ceil((totalRecords || 0) / (itemsPerPage || 10))))
                                if (!isNaN(page)) {
                                    onPageChange && onPageChange(page)
                                    setJumpPage(page)
                                }
                            }}
                            style={{
                                width: '56px',
                                height: '28px',
                                textAlign: 'center',
                                fontSize: '13px',
                                border: '1px solid #dcdfe6',
                                borderRadius: '4px',
                                outline: 'none',
                                padding: '0 4px',
                                // Hide spin buttons so the full width is usable for typing
                                MozAppearance: 'textfield',
                            }}
                            className='jump-page-input'
                        />
                    </div>
                    <TablePagination
                        rowsPerPageOptions={[]}
                        component='div'
                        count={totalRecords || 0}
                        rowsPerPage={itemsPerPage || 10}
                        page={(currentPage || 1) - 1}
                        onPageChange={(_, page) => onPageChange && onPageChange(page + 1)}
                        onRowsPerPageChange={e => {
                            const val = Number(e.target.value);
                            if (onItemsPerPageChange) onItemsPerPageChange(val);
                        }}
                        nextIconButtonProps={(globalFilter || externalGlobalFilter) ? { disabled: true } : undefined}
                        sx={{
                            border: 'none',
                            '& .MuiTablePagination-toolbar': { minHeight: '40px' },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon, & .MuiInputBase-root': { display: 'none !important' },
                            '& .MuiTablePagination-displayedRows': { fontSize: '12px', opacity: 0.7, margin: 0 }
                        }}
                    />
                </div>
            </Card>
        </>
    )
}

export default PremiumModuleTable
